import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { MOCK_WIDGETS_RESPONSE } from '../shared/mocks/widgets.mock';

import { HOME_QUERY } from '../shared/graphql/home.query';
import { WeatherWidget, Widget, WidgetSize } from '../models/widget.model';
import { HomeQueryResponse } from '../models/homepage.model';
import { ApiEndpointsService } from './api-endpoints.service';
import { UserWeatherSettings } from '../models/weather-widget.model';

@Injectable({ providedIn: 'root' })
export class HomeService {
  private readonly http = inject(HttpClient);

  // Replace with real endpoint when available and move to environment.ts
  private readonly apiService = inject(ApiEndpointsService);
  
  private homepage$ = this.http
    .post<HomeQueryResponse>(
      this.apiService.baseUrl,
      { query: HOME_QUERY },
      { withCredentials: true }
    )
    .pipe(shareReplay(1));

  getHomepage(): Observable<HomeQueryResponse> {
     return this.homepage$;
  }
    
  getWidgets() {
    // Uncomment for mock data
    // return of(MOCK_WIDGETS_RESPONSE.data.homepage.widgets as Widget[]);
    return this.getHomepage().pipe(
      map((res) => res?.data?.homepage?.widgets ?? []),
      catchError((err) => {
        console.error('Failed to load widgets', err);
        return of<Widget[]>([]);
      })
    );
  }

  getHasCOM(): Observable<boolean> {
    return this.getHomepage().pipe(
      map((res) => res?.data?.homepage?.widgets ?? []),
      map((widgets: Widget[]) => widgets.some((w) => w.__typename === 'COMWidget')),
      catchError((err) => {
        console.error('Failed to load widgets', err);
        return of(false);
      })
    );
  }

  /**
   * Get user weather settings from the api
   * @returns Observable of UserWeatherSettings or null if not found
   */
  getWeatherSettings(): Observable<UserWeatherSettings | null> {
    return this.getHomepage().pipe(
      map(res => {
        const widgets = res?.data?.homepage?.widgets ?? [];
        const weatherWidget = widgets.find( (w): w is WeatherWidget => w.__typename === 'WeatherWidget')
        return weatherWidget?.settings ?? null;
      })
    );
  }
}
